import { Inter } from 'next/font/google';
import { IoArrowBack, IoArrowForward } from 'react-icons/io5';

import { createTheme } from '@mantine/core';

const inter = Inter({ subsets: ['latin'] });

import rteClasses from './rte.module.scss';
import cx from './theme.module.scss';

export const theme = createTheme({
    fontFamily: `${inter.style.fontFamily}, sans-serif`,
    primaryColor: 'gray',
    primaryShade: 7,
    cursorType: 'pointer',
    focusRing: 'always',
    defaultRadius: 'sm',
    white: '#ffffff',
    black: '#000000',
    colors: {
        // blue
        primary: [
            '#E3F9FF',
            '#C3F1FF',
            '#A3E9FC',
            '#6BE9FC', // base color
            '#58C8E4',
            '#46A9D1',
            '#3388B6',
            '#216B94',
            '#1A4F7A',
            '#103B5F',
        ],
        // purple
        brand: [
            '#eaf2ff',
            '#d6e1fa',
            '#adc1ee',
            '#809ee3',
            '#5b81da',
            '#436fd5',
            '#3566d4',
            '#2a5ccb', // base color
            '#1e4ca9',
            '#0e4196',
        ],
        brandOriginal: [
            '#D3D1F8',
            '#B5AEEF',
            '#9791E6',
            '#7A76DB',
            '#5E5AC8',
            '#4A47B3',
            '#38339E',
            '#2E2C97',
            '#252586',
            '#1A1A74',
        ],
        connection: Array(10).fill('#868E96') as any,
        AC: Array(10).fill('#D9480F') as any,
        DC: Array(10).fill('#1864AB') as any,
    },
    defaultGradient: {
        deg: 130,
        from: 'primary.4',
        to: 'brandOriginal.7',
    },
    fontSizes: {
        xs: '0.6875rem',
        sm: '0.75rem',
        md: '0.8125rem',
        lg: '1rem',
        xl: '1.125rem',
    },
    headings: {
        fontFamily: `${inter.style.fontFamily}, sans-serif`,
        fontWeight: '500',
        sizes: {
            h1: {
                fontSize: '1.125rem',
                fontWeight: '600',
            },
            h2: {
                fontSize: '1rem',
                fontWeight: '600',
            },
            h3: {
                fontSize: '0.8125rem',
                fontWeight: '500',
            },
            h4: {
                fontSize: '0.75rem',
                fontWeight: '500',
            },
        },
    },
    breakpoints: {
        xs: '30em',
        sm: '48em',
        md: '64em',
        lg: '74em',
        xl: '90em',
        xxl: '100em',
        mobileNav: '1100px',
    },
    components: {
        Button: {
            classNames: {
                root: cx.button,
            },
            defaultProps: {
                color: 'black',
            },
        },
        ActionIcon: {
            defaultProps: {
                color: 'black',
            },
        },
        ThemeIcon: {
            defaultProps: {
                color: 'black',
            },
        },
        Badge: {
            defaultProps: {
                color: 'black',
            },
        },
        Modal: {
            defaultProps: {
                closeButtonProps: {
                    size: 'sm',
                },
                zIndex: 204,
            },
            styles: {
                title: {
                    fontWeight: 600,
                    fontSize: 'var(--mantine-h1-font-size)',
                },
                body: {
                    paddingLeft: 'var(--mantine-spacing-lg)',
                    paddingRight: 'var(--mantine-spacing-lg)',
                    paddingBottom: 'var(--mantine-spacing-lg)',
                },
                header: {
                    paddingTop: 'var(--mantine-spacing-lg)',
                    paddingLeft: 'var(--mantine-spacing-lg)',
                    paddingRight: 'var(--mantine-spacing-lg)',
                },
            },
        },
        Card: {
            classNames: {
                root: cx.card,
            },
        },
        Anchor: {
            classNames: {
                root: cx.link,
            },
        },
        ButtonGroup: {
            classNames: {
                group: cx.buttonGroup,
            },
        },
        TextInput: {
            classNames: {
                root: cx.inputWrapper,
            },
        },
        Textarea: {
            classNames: {
                root: cx.inputWrapper,
            },
        },
        NumberInput: {
            classNames: {
                root: cx.inputWrapper,
            },
        },
        Select: {
            classNames: {
                root: cx.inputWrapper,
                groupLabel: cx.groupLabel,
                option: cx.option,
            },
        },
        MultiSelect: {
            classNames: {
                input: [cx.input, cx.pillsInput],
                groupLabel: cx.groupLabel,
                option: cx.option,
            },
        },
        Checkbox: {
            defaultProps: {
                color: 'black',
            },
        },
        Radio: {
            defaultProps: {
                color: 'black',
            },
        },
        ColorInput: {
            classNames: {
                root: cx.inputWrapper,
            },
        },
        InputBase: {
            classNames: {
                root: cx.inputWrapper,
            },
        },
        Autocomplete: {
            classNames: {
                root: cx.inputWrapper,
            },
        },
        DateInput: {
            classNames: {
                root: cx.inputWrapper,
            },
        },
        PillsInput: {
            classNames: {
                input: [cx.input, cx.pillsInput],
            },
        },
        TagsInput: {
            classNames: {
                input: [cx.input, cx.pillsInput],
            },
        },
        RichTextEditor: {
            classNames: rteClasses,
        },
        Carousel: {
            defaultProps: {
                color: 'white',
                nextControlIcon: <IoArrowForward />,
                previousControlIcon: <IoArrowBack />,
            },
        },
        Loader: {
            defaultProps: {
                color: 'black',
            },
        },
        Pagination: {
            defaultProps: {
                color: 'black',
                hideWithOnePage: true,
                radius: 'xs',
            },
        },
        Progress: {
            defaultProps: {
                color: 'black',
            },
        },
        Tooltip: {
            defaultProps: {
                radius: 0,
                bg: 'black',
                fz: 'xs',
                px: 6,
                py: 3,
            },
        },
        TooltipFloating: {
            defaultProps: {
                radius: 0,
                bg: 'black',
                fz: 'xs',
                px: 6,
                py: 3,
            },
        },
        RangeSlider: {
            defaultProps: {
                color: 'black',
            },
        },
        Stepper: {
            defaultProps: {
                color: 'black',
            },
        },
    },
});
